import React, { useState, useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import EmailLogin from './components/EmailLogin'
import MagicLinkSent from './components/MagicLinkSent'
import MagicLinkAuth from './components/MagicLinkAuth'
import PartnerLayout from './components/PartnerLayout'
import ApiDocs from './components/docs/ApiDocs'
import IntegrationSelector from './components/docs/IntegrationSelector'
import apiService from './services/api'
import { Partner } from './types/partner'

function App() {
    const [partner, setPartner] = useState<Partner | null>(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        // Check if there's an existing session
        const checkSession = async () => {
            const token = localStorage.getItem('partnerToken')
            const storedPartner = localStorage.getItem('partner')

            if (token && storedPartner) {
                try {
                    // Validate session with backend
                    const response = await apiService.validateSession()

                    if (response.success && response.data?.valid) {
                        setPartner(JSON.parse(storedPartner))
                    } else {
                        // Session invalid, clear storage
                        localStorage.removeItem('partnerToken')
                        localStorage.removeItem('partner')
                    }
                } catch (error) {
                    console.error('Session validation error:', error)
                    localStorage.removeItem('partnerToken')
                    localStorage.removeItem('partner')
                }
            }

            setLoading(false)
        }

        checkSession()
    }, [])

    const handleAuthenticated = (sessionToken: string, partnerData: Partner) => {
        setPartner(partnerData)
    }

    if (loading) {
        return (
            <div style={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
            }}>
                <div style={{
                    textAlign: 'center',
                    color: 'white'
                }}>
                    <div style={{
                        width: '48px',
                        height: '48px',
                        border: '4px solid rgba(255, 255, 255, 0.3)',
                        borderTopColor: 'white',
                        borderRadius: '50%',
                        margin: '0 auto 16px',
                        animation: 'spin 0.8s linear infinite'
                    }}></div>
                    <p style={{ fontSize: '16px', fontWeight: 500 }}>Loading...</p>
                </div>
            </div>
        )
    }

    return (
        <Router>
            <Routes>
                {/* Public routes */}
                <Route path="/" element={
                    partner ? <Navigate to="/docs" replace /> : <EmailLogin />
                } />

                <Route path="/sent" element={<MagicLinkSent />} />

                <Route path="/auth" element={
                    <MagicLinkAuth onAuthenticated={handleAuthenticated} />
                } />

                {/* Protected routes */}
                <Route path="/docs" element={
                    partner ? (
                        <PartnerLayout partner={{
                            companyName: partner.companyName,
                            email: partner.email
                        }}>
                            <IntegrationSelector />
                        </PartnerLayout>
                    ) : (
                        <Navigate to="/" replace />
                    )
                } />

                <Route path="/docs/api" element={
                    partner ? (
                        <PartnerLayout partner={{
                            companyName: partner.companyName,
                            email: partner.email
                        }}>
                            <ApiDocs initialDocType="api" />
                        </PartnerLayout>
                    ) : (
                        <Navigate to="/" replace />
                    )
                } />

                <Route path="/docs/webview" element={
                    partner ? (
                        <PartnerLayout partner={{
                            companyName: partner.companyName,
                            email: partner.email
                        }}>
                            <ApiDocs initialDocType="webview" />
                        </PartnerLayout>
                    ) : (
                        <Navigate to="/" replace />
                    )
                } />

                {/* Catch all - redirect to home */}
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </Router>
    )
}

export default App
